/**
 * RelatedCategories  Functional Component
 * @Package - seo/part
 * @Author - Cybage@UPWA-Team
 * @description: RelatedCategories related to part SEO
 */
/**
 *  Part SEO's RelatedCategories Component
 */
import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react'
import {
  AddIcon, Box, IcoMoon, RemoveIcon, useResponsiveWrapperContext,
} from '@usap/uicomponents'
import { Title, PartListing } from '@usap/widgets'
import styled from 'styled-components'
import { LocalContext } from '../../helpers/useLocalContext'
import RelatedPartsProvider, { RelatedPartsContext } from '../../contexts/relatedParts.context'

const CollapseBox = styled(Box)`
  cursor: pointer;
`
const MainContainer = styled(Box)`
  padding: 12px;
`

const HeadingSection = styled(Box)`
  @media (max-width: 1024px) {
    flex-direction: row;
    align-self: flex-start;
    justify-content: space-between;
    width: 100%;
    align-items: center;
    margin-left: 0;
    margin-right: 0;
  }
`
const RelatedPartsComponent = ({ dynamicApiData }) => {
  const { isDesktop } = useResponsiveWrapperContext()
  const { localContext } = useContext(LocalContext)
  const { isAmp, isBot, Link } = localContext
  const colWidth = isDesktop ? '16.66%' : '33.33%'
  const relatedPartsCon = useContext(RelatedPartsContext)
  const relParts = useMemo(
    () => dynamicApiData.relatedParts || relatedPartsCon || [],
    [relatedPartsCon, dynamicApiData],
  )
  const [isDisplay, setIsDisplay] = useState(true)
  const title = dynamicApiData?.headings?.relatedParts || 'Related Parts'

  useEffect(() => {
    if (isDesktop) {
      setIsDisplay(true)
    } else {
      setIsDisplay(false)
    }
  }, [isDesktop])

  const styleMemo = useMemo(
    () => ({
      box1Marg: { horizontal: 'auto', vertical: '0px' },
      box1Pad: { horizontal: 'small', top: 'medium', bottom: 'large' },
      box2Marg: {
        horizontal: 'small',
        bottom: isDisplay && !isDesktop ? 'medium' : null,
      },
      headerBox: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
      },
      titleSize: isDesktop ? 'medium' : 'small',
    }),
    [isDesktop, isDisplay],
  )

  const handleAccordion = useCallback(() => {
    if (isDisplay) {
      setIsDisplay(false)
    } else {
      setIsDisplay(true)
    }
  }, [isDisplay])

  if (!relParts || relParts.length === 0) {
    return null
  }

  return (
    <Box background="light-1">
      <MainContainer
        id="SeoRelatedParts"
        width="1248px"
        margin={styleMemo.box1Marg}
      >
        <HeadingSection margin={styleMemo.box2Marg} style={styleMemo.headerBox}>
          <Title
            type="h2"
            size="20px"
            label={title}
          />
          {
            !isDesktop && (
              <CollapseBox onClick={handleAccordion}>
                <IcoMoon icon={isDisplay ? RemoveIcon : AddIcon} color="#393939" />
              </CollapseBox>
            )
          }
        </HeadingSection>
        {
          isDisplay && (
            <PartListing
              parts={relParts}
              isAmp={isAmp}
              colWidth={colWidth}
              Link={Link}
              isBot={isBot}
              noLazy
            />
          )
        }
      </MainContainer>
    </Box>
  )
}

export default function SeoRelatedParts({ dynamicApiData }) {
  const {
    pageType,
    products = {},
    selectedAttributes = {},
    headings = {},
    relatedParts,
  } = dynamicApiData

  const attributes = useMemo(() => ({
    year: selectedAttributes.year || '',
    make: selectedAttributes.make || '',
    model: selectedAttributes.model || '',
    part: (products && products.selectedPart) || '',
  }), [products, selectedAttributes])

  const apiData = useMemo(() => ({ relatedParts, headings }), [headings, relatedParts])

  const topLevel = products?.items[0]?.topLevelCategory ?? ''
  const secondLevel = products?.items[0]?.category ?? ''

  return (
    <RelatedPartsProvider
      limit="6"
      pageType={pageType}
      attribute={attributes}
      relatedParts={relatedParts}
      topLevel={topLevel}
      secondLevel={secondLevel}
    >
      <RelatedPartsComponent
        dynamicApiData={apiData}
      />
    </RelatedPartsProvider>
  )
}
